import { useState, useEffect, useContext } from "react";
import { Heart, DollarSign, Plus, Trash2, Edit2, Save, X, Gift, MessageSquare, UserPlus, Star, Sparkles } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { AuthContext } from "../App";

// Tip Menu Editor - For employees to edit their menu
export const TipMenuEditor = ({ API, token, onClose }) => {
  const [tipMenu, setTipMenu] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newItem, setNewItem] = useState({ name: "", price: 10, description: "" });

  useEffect(() => {
    fetchTipMenu();
  }, []);

  const fetchTipMenu = async () => {
    try {
      const res = await axios.get(`${API}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Get employee's tip menu
      const empRes = await axios.get(`${API}/employees/${res.data.user_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => null);
      
      if (empRes?.data?.employee?.tip_menu) {
        setTipMenu(empRes.data.employee.tip_menu);
      }
    } catch (error) {
      console.error("Error fetching tip menu:", error);
    }
    setLoading(false);
  };

  const addItem = () => {
    if (!newItem.name.trim()) {
      toast.error("Item name is required");
      return;
    }
    setTipMenu([...tipMenu, { ...newItem, id: Date.now().toString() }]);
    setNewItem({ name: "", price: 10, description: "" });
  };

  const removeItem = (index) => {
    const updated = [...tipMenu];
    updated.splice(index, 1);
    setTipMenu(updated);
  };

  const saveMenu = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/employees/tip-menu`, tipMenu, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Tip menu saved!");
      onClose?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save menu");
    }
    setSaving(false);
  };

  if (loading) {
    return <div className="text-center py-8 text-gray-500">Loading...</div>;
  }

  return (
    <div className="p-4 bg-gradient-to-b from-pink-900/20 to-purple-900/20 rounded-lg">
      <h3 className="text-lg font-bold text-pink-400 mb-4 flex items-center gap-2">
        <Gift className="w-5 h-5" /> Edit Your Tip Menu
      </h3>

      {/* Current Items */}
      <div className="space-y-2 mb-4 max-h-60 overflow-y-auto">
        {tipMenu.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No items yet. Add your first tip menu item below!</p>
        ) : (
          tipMenu.map((item, index) => (
            <div key={item.id || index} className="flex items-center gap-2 bg-gray-800 p-2 rounded">
              <div className="flex-1">
                <div className="text-white font-bold text-sm">{item.name}</div>
                {item.description && (
                  <div className="text-gray-400 text-xs">{item.description}</div>
                )}
              </div>
              <div className="text-yellow-400 font-bold">💎 {item.price}</div>
              <button
                onClick={() => removeItem(index)}
                className="p-1 text-red-500 hover:bg-red-900/30 rounded"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))
        )}
      </div>

      {/* Add New Item */}
      <div className="bg-gray-800 p-3 rounded mb-4">
        <div className="grid grid-cols-3 gap-2 mb-2">
          <input
            type="text"
            placeholder="Item name..."
            value={newItem.name}
            onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
            className="col-span-2 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm"
          />
          <input
            type="number"
            placeholder="Price"
            value={newItem.price}
            onChange={(e) => setNewItem({ ...newItem, price: parseInt(e.target.value) || 0 })}
            className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm"
            min="1"
            max="10000"
          />
        </div>
        <input
          type="text"
          placeholder="Description (optional)..."
          value={newItem.description}
          onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
          className="w-full px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm mb-2"
        />
        <button
          onClick={addItem}
          className="w-full py-1.5 bg-green-600 hover:bg-green-500 rounded text-white text-sm font-bold flex items-center justify-center gap-1"
        >
          <Plus className="w-4 h-4" /> Add Item
        </button>
      </div>

      {/* Save Button */}
      <button
        onClick={saveMenu}
        disabled={saving}
        className="w-full py-2 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-500 hover:to-purple-500 rounded text-white font-bold flex items-center justify-center gap-2"
      >
        <Save className="w-4 h-4" />
        {saving ? "Saving..." : "Save Tip Menu"}
      </button>
    </div>
  );
};

// Employee Profile Card - Shows tip menu and allows tipping
export const EmployeeProfileCard = ({ API, token, employeeUserId, onClose, onAddFriend }) => {
  const { user } = useContext(AuthContext);
  const [employee, setEmployee] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tipping, setTipping] = useState(false);
  const [customTip, setCustomTip] = useState(10);
  const [tipMessage, setTipMessage] = useState("");
  const [showLoveNote, setShowLoveNote] = useState(false);
  const [loveNoteText, setLoveNoteText] = useState("");
  const [loveNotes, setLoveNotes] = useState([]);
  const [sendingNote, setSendingNote] = useState(false);

  useEffect(() => {
    fetchEmployee();
    fetchLoveNotes();
  }, [employeeUserId]);

  const fetchEmployee = async () => {
    try {
      const res = await axios.get(`${API}/employees/${employeeUserId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployee(res.data.employee);
      setUserProfile(res.data.user);
    } catch (error) {
      console.error("Error fetching employee:", error);
    }
    setLoading(false);
  };

  const fetchLoveNotes = async () => {
    try {
      const res = await axios.get(`${API}/users/${employeeUserId}/love-notes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLoveNotes(res.data.love_notes || []);
    } catch (error) {
      console.error("Error fetching love notes:", error);
    }
  };

  const tipEmployee = async (itemId = null, amount = null) => {
    setTipping(true);
    try {
      const payload = { message: tipMessage };
      if (itemId) {
        payload.item_id = itemId;
      } else {
        payload.amount = amount || customTip;
      }
      
      const res = await axios.post(`${API}/employees/${employeeUserId}/tip`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      setTipMessage("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to tip");
    }
    setTipping(false);
  };

  const sendLoveNote = async () => {
    if (!loveNoteText.trim()) {
      toast.error("Please write a message");
      return;
    }
    setSendingNote(true);
    try {
      const res = await axios.post(`${API}/users/${employeeUserId}/love-note`, 
        { message: loveNoteText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      setLoveNoteText("");
      setShowLoveNote(false);
      fetchLoveNotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send love note");
    }
    setSendingNote(false);
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-gray-500">
        <Sparkles className="w-8 h-8 mx-auto mb-2 animate-spin" />
        Loading...
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="p-8 text-center text-gray-500">
        Employee not found
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-b from-pink-900/30 to-purple-900/30 rounded-lg overflow-hidden max-w-md mx-auto">
      {/* Header */}
      <div className="bg-gradient-to-r from-pink-600 to-purple-600 p-4 relative">
        {onClose && (
          <button onClick={onClose} className="absolute top-2 right-2 text-white/80 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        )}
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-pink-400 to-purple-400 flex items-center justify-center text-3xl border-2 border-white/50">
            {userProfile?.gender === 'female' ? '👩' : '👨'}
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">{employee.display_name}</h2>
            <p className="text-pink-200 text-sm">@{employee.username}</p>
            <div className="flex items-center gap-2 mt-1">
              <span className="bg-yellow-500 text-yellow-900 px-2 py-0.5 rounded text-xs font-bold">
                ⭐ PERFORMER
              </span>
              <span className="text-pink-200 text-xs">
                💎 {employee.total_tips_received?.toLocaleString() || 0} earned
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex gap-2 p-3 bg-gray-800 border-b border-gray-700">
        <button
          onClick={() => onAddFriend?.(employeeUserId)}
          className="flex-1 py-2 bg-blue-600 hover:bg-blue-500 rounded text-white text-sm font-bold flex items-center justify-center gap-1"
        >
          <UserPlus className="w-4 h-4" /> Add Friend
        </button>
        <button
          onClick={() => setShowLoveNote(true)}
          className="flex-1 py-2 bg-pink-600 hover:bg-pink-500 rounded text-white text-sm font-bold flex items-center justify-center gap-1"
        >
          <Heart className="w-4 h-4" /> Love Note (10💎)
        </button>
      </div>

      {/* Love Note Form */}
      {showLoveNote && (
        <div className="p-3 bg-pink-900/30 border-b border-pink-800">
          <textarea
            value={loveNoteText}
            onChange={(e) => setLoveNoteText(e.target.value)}
            placeholder="Write your love note... 💕"
            className="w-full p-2 bg-gray-800 border border-pink-600 rounded text-white text-sm resize-none"
            rows={3}
            maxLength={500}
          />
          <div className="flex gap-2 mt-2">
            <button
              onClick={() => setShowLoveNote(false)}
              className="flex-1 py-1.5 bg-gray-700 hover:bg-gray-600 rounded text-white text-sm"
            >
              Cancel
            </button>
            <button
              onClick={sendLoveNote}
              disabled={sendingNote}
              className="flex-1 py-1.5 bg-pink-600 hover:bg-pink-500 rounded text-white text-sm font-bold"
            >
              {sendingNote ? "Sending..." : "Send 💕 (10💎)"}
            </button>
          </div>
        </div>
      )}

      {/* Tip Menu */}
      <div className="p-3">
        <h3 className="text-pink-400 font-bold mb-2 flex items-center gap-2">
          <Gift className="w-4 h-4" /> Tip Menu
        </h3>
        
        {employee.tip_menu?.length > 0 ? (
          <div className="space-y-2 max-h-48 overflow-y-auto mb-3">
            {employee.tip_menu.map((item, index) => (
              <button
                key={item.id || index}
                onClick={() => tipEmployee(item.id)}
                disabled={tipping}
                className="w-full p-2 bg-gray-800 hover:bg-gray-700 rounded flex items-center justify-between group transition-colors"
              >
                <div className="text-left">
                  <div className="text-white font-bold text-sm">{item.name}</div>
                  {item.description && (
                    <div className="text-gray-400 text-xs">{item.description}</div>
                  )}
                </div>
                <div className="bg-yellow-600 group-hover:bg-yellow-500 px-3 py-1 rounded text-white font-bold text-sm">
                  💎 {item.price}
                </div>
              </button>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-sm mb-3">No tip menu items yet</p>
        )}

        {/* Custom Tip */}
        <div className="bg-gray-800 p-3 rounded">
          <div className="text-white text-sm font-bold mb-2">Custom Tip</div>
          <div className="flex gap-2 mb-2">
            {[5, 10, 25, 50, 100].map(amt => (
              <button
                key={amt}
                onClick={() => setCustomTip(amt)}
                className={`flex-1 py-1 rounded text-sm font-bold ${
                  customTip === amt 
                    ? 'bg-yellow-600 text-white' 
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {amt}
              </button>
            ))}
          </div>
          <input
            type="text"
            placeholder="Add a message (optional)..."
            value={tipMessage}
            onChange={(e) => setTipMessage(e.target.value)}
            className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm mb-2"
          />
          <button
            onClick={() => tipEmployee(null, customTip)}
            disabled={tipping}
            className="w-full py-2 bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-500 hover:to-orange-500 rounded text-white font-bold flex items-center justify-center gap-2"
          >
            <DollarSign className="w-4 h-4" />
            {tipping ? "Sending..." : `Tip ${customTip} Diamonds`}
          </button>
        </div>
      </div>

      {/* Love Notes Section */}
      {loveNotes.length > 0 && (
        <div className="p-3 border-t border-gray-700">
          <h3 className="text-pink-400 font-bold mb-2 flex items-center gap-2">
            <Heart className="w-4 h-4" /> Love Notes ({loveNotes.length})
          </h3>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {loveNotes.slice(0, 5).map((note, index) => (
              <div key={note.note_id || index} className="bg-pink-900/30 p-2 rounded">
                <div className="text-pink-300 text-xs mb-1">
                  💕 From <span className="font-bold">{note.from_username}</span>
                </div>
                <div className="text-white text-sm">{note.message}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeProfileCard;
